import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PlayArrow,
  Visibility,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
} from '@mui/icons-material';
import APIKit from '../../../config/APIKit';
import { showSnackBar } from '../../store/compatibility';
import { useDispatch } from 'react-redux';
import style from '../settings/settings.module.scss';
import Title from '../settings/Title';
import ConfirmationDialog from '../../component/ConfirmationDialog';

export default function WebhookManagement() {
  const dispatch = useDispatch();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, webhook: null, mode: 'create' });
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: null,
    loading: false,
  });

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const response = await APIKit.get('/webhooks');
      if (response.success) {
        setWebhooks(response.data || []);
      }
    } catch (error) {
      console.error('Error loading webhooks:', error);
      dispatch(showSnackBar({ message: 'Error loading webhooks', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const loadWebhookEvents = async (webhookId) => {
    try {
      const response = await APIKit.get(`/webhooks/${webhookId}/events`);
      if (response.success) {
        setEvents(response.data || []);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const handleCreate = () => {
    setModal({ open: true, webhook: null, mode: 'create' });
  };

  const handleEdit = (webhook) => {
    setModal({ open: true, webhook, mode: 'edit' });
  };

  const openConfirmDialog = (webhookId) => {
    setConfirmDialog({
      open: true,
      id: webhookId,
      loading: false,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      id: null,
      loading: false,
    });
  };

  const handleDelete = async () => {
    if (!confirmDialog.id) return;
    
    setConfirmDialog(prev => ({
      ...prev,
      loading: true,
    }));

    try {
      await APIKit.delete(`/webhooks/${confirmDialog.id}`);
      dispatch(showSnackBar({ message: 'Webhook deleted successfully', severity: 'success' }));
      loadWebhooks();
      closeConfirmDialog();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error deleting webhook', severity: 'error' }));
      setConfirmDialog(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleTest = async (webhookId) => {
    try {
      await APIKit.post(`/webhooks/${webhookId}/test`, {
        test_payload: {
          test: true,
          timestamp: new Date().toISOString(),
        },
      });
      dispatch(showSnackBar({ message: 'Webhook test sent successfully', severity: 'success' }));
      loadWebhooks();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error testing webhook', severity: 'error' }));
    }
  };

  const handleViewEvents = (webhook) => {
    setSelectedWebhook(webhook);
    setTab(1);
    loadWebhookEvents(webhook.id);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" fontSize="small" />;
      case 'failed':
        return <Cancel color="error" fontSize="small" />;
      case 'pending':
        return <Pending color="warning" fontSize="small" />;
      default:
        return <Pending fontSize="small" />;
    }
  };

  return (
    <div className={style.container}>
      <Title
        title="Webhook Management"
        handleModal={handleCreate}
        enableButton={true}
      />

      <Box sx={{ mt: 2 }}>
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          sx={{
            mb: 3,
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              height: '3px',
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            label="Webhooks"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              '&.Mui-selected': {
                color: '#667eea',
              },
            }}
          />
          {selectedWebhook && (
            <Tab
              label={`Events: ${selectedWebhook.name}`}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '15px',
                '&.Mui-selected': {
                  color: '#667eea',
                },
              }}
            />
          )}
        </Tabs>

        {tab === 0 && (
          <Box
            sx={{
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                zIndex: 1,
              },
            }}
          >
            <TableContainer sx={{ px: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 3, fontWeight: 700, color: '#667eea' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>URL</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Entity Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Trigger Events</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Success/Failed</TableCell>
                    <TableCell align="right" sx={{ pr: 3, fontWeight: 700, color: '#667eea' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">No webhooks configured</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    webhooks.map(webhook => (
                      <TableRow
                        key={webhook.id}
                        sx={{
                          '&:hover': {
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                          },
                        }}
                      >
                        <TableCell sx={{ pl: 3 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {webhook.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', color: '#666' }}>
                            {webhook.url}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={webhook.entity_type}
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                              color: '#667eea',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {webhook.trigger_events?.slice(0, 2).map(event => (
                              <Chip
                                key={event}
                                label={event}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: 'rgba(102, 126, 234, 0.3)',
                                  color: '#667eea',
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                            {webhook.trigger_events?.length > 2 && (
                              <Chip
                                label={`+${webhook.trigger_events.length - 2}`}
                                size="small"
                                sx={{
                                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                  color: '#667eea',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={webhook.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              background: webhook.is_active
                                ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                : 'rgba(0, 0, 0, 0.1)',
                              color: webhook.is_active ? '#ffffff' : '#666',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                            {webhook.success_count} / {webhook.failure_count}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <Tooltip title="View Events">
                            <IconButton
                              size="small"
                              onClick={() => handleViewEvents(webhook)}
                              sx={{
                                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                color: '#667eea',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: '#ffffff',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Test">
                            <IconButton
                              size="small"
                              onClick={() => handleTest(webhook.id)}
                              sx={{
                                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.1) 100%)',
                                color: '#4caf50',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                  color: '#ffffff',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                                },
                              }}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(webhook)}
                            sx={{
                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                              color: '#667eea',
                              borderRadius: '8px',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: '#ffffff',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                              },
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openConfirmDialog(webhook.id)}
                            sx={{
                              background: 'linear-gradient(135deg, rgba(239, 83, 80, 0.1) 0%, rgba(229, 57, 53, 0.1) 100%)',
                              color: '#ef5350',
                              borderRadius: '8px',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                                color: '#ffffff',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(239, 83, 80, 0.3)',
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && selectedWebhook && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Webhook Events: {selectedWebhook.name}
              </Typography>
              <IconButton
                onClick={() => loadWebhookEvents(selectedWebhook.id)}
                sx={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  color: '#667eea',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#ffffff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
            <Box
              sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(102, 126, 234, 0.1)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  zIndex: 1,
                },
              }}
            >
              <TableContainer sx={{ px: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 3, fontWeight: 700, color: '#667eea' }}>Event Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Entity</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Response</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#667eea' }}>Attempts</TableCell>
                      <TableCell sx={{ pr: 3, fontWeight: 700, color: '#667eea' }}>Triggered At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">No events yet</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      events.map(event => (
                        <TableRow
                          key={event.id}
                          sx={{
                            '&:hover': {
                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                            },
                          }}
                        >
                          <TableCell sx={{ pl: 3 }}>{event.event_type}</TableCell>
                          <TableCell>
                            {event.entity_type}: {event.entity_id?.substring(0, 8)}...
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getStatusIcon(event.status)}
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{event.status}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {event.response_status ? (
                              <Chip
                                label={event.response_status}
                                size="small"
                                sx={{
                                  background: event.response_status >= 200 && event.response_status < 300
                                    ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                    : 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                                  color: '#ffffff',
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#666' }}>{event.attempt_count}</TableCell>
                          <TableCell sx={{ pr: 3, color: '#666' }}>
                            {new Date(event.triggered_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={handleDelete}
        title="Delete Webhook?"
        message="Are you sure you want to delete this webhook? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
        confirmColor="error"
        loading={confirmDialog.loading}
      />

      <WebhookModal
        open={modal.open}
        webhook={modal.webhook}
        mode={modal.mode}
        onClose={() => setModal({ open: false, webhook: null, mode: 'create' })}
        onSave={loadWebhooks}
      />
    </div>
  );
}

// Webhook Create/Edit Modal
function WebhookModal({ open, webhook, mode, onClose, onSave }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    method: 'POST',
    event_type: 'outgoing',
    entity_type: 'invoice',
    trigger_events: [],
    authentication_type: 'none',
    authentication_config: {},
    retry_count: 3,
    timeout_seconds: 30,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (webhook) {
      setFormData({
        name: webhook.name || '',
        description: webhook.description || '',
        url: webhook.url || '',
        method: webhook.method || 'POST',
        event_type: webhook.event_type || 'outgoing',
        entity_type: webhook.entity_type || 'invoice',
        trigger_events: webhook.trigger_events || [],
        authentication_type: webhook.authentication_type || 'none',
        authentication_config: webhook.authentication_config || {},
        retry_count: webhook.retry_count || 3,
        timeout_seconds: webhook.timeout_seconds || 30,
        is_active: webhook.is_active !== undefined ? webhook.is_active : true,
      });
    }
  }, [webhook]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (mode === 'create') {
        await APIKit.post('/webhooks', formData);
        dispatch(showSnackBar({ message: 'Webhook created successfully', severity: 'success' }));
      } else {
        await APIKit.post(`/webhooks/${webhook.id}`, formData);
        dispatch(showSnackBar({ message: 'Webhook updated successfully', severity: 'success' }));
      }
      onSave();
      onClose();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error saving webhook', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const availableEvents = ['created', 'updated', 'deleted', 'status_changed', 'paid', 'approved', 'rejected'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '20px',
          py: 2.5,
          px: 3,
          mb: 0,
        }}
      >
        {mode === 'create' ? 'Create Webhook' : 'Edit Webhook'}
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 3, overflow: 'visible' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                },
              }}
            />
          </Box>
          <Box>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                },
              }}
            />
          </Box>
          <Box>
            <TextField
              label="Webhook URL"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              fullWidth
              required
              placeholder="https://your-app.com/webhook"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                },
              }}
            />
          </Box>

          <Grid container spacing={1.5}>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="method-label"
                    sx={{
                      '&.MuiInputLabel-root': {
                        backgroundColor: 'transparent',
                        px: 0.5,
                        transform: 'translate(14px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                  >
                    HTTP Method
                  </InputLabel>
                  <Select
                    labelId="method-label"
                    value={formData.method}
                    onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                    label="HTTP Method"
                    sx={{
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                        },
                      },
                    }}
                  >
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="entity-type-label"
                    sx={{
                      '&.MuiInputLabel-root': {
                        backgroundColor: 'transparent',
                        px: 0.5,
                        transform: 'translate(14px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                  >
                    Entity Type
                  </InputLabel>
                  <Select
                    labelId="entity-type-label"
                    value={formData.entity_type}
                    onChange={(e) => setFormData({ ...formData, entity_type: e.target.value })}
                    label="Entity Type"
                    sx={{
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                        },
                      },
                    }}
                  >
                    <MenuItem value="invoice">Invoice</MenuItem>
                    <MenuItem value="client">Client</MenuItem>
                    <MenuItem value="expense">Expense</MenuItem>
                    <MenuItem value="project">Project</MenuItem>
                    <MenuItem value="task">Task</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <FormControl fullWidth>
              <InputLabel
                id="trigger-events-label"
                sx={{
                  '&.MuiInputLabel-root': {
                    backgroundColor: 'transparent',
                    px: 0.5,
                    transform: 'translate(14px, 16px) scale(1)',
                  },
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                }}
              >
                Trigger Events
              </InputLabel>
              <Select
                labelId="trigger-events-label"
                multiple
                value={formData.trigger_events}
                onChange={(e) => setFormData({ ...formData, trigger_events: e.target.value })}
                label="Trigger Events"
                sx={{
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '12px',
                  },
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                    },
                  },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availableEvents.map(event => (
                  <MenuItem key={event} value={event}>
                    {event}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <FormControl fullWidth>
              <InputLabel
                id="auth-type-label"
                sx={{
                  '&.MuiInputLabel-root': {
                    backgroundColor: 'transparent',
                    px: 0.5,
                    transform: 'translate(14px, 16px) scale(1)',
                  },
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                }}
              >
                Authentication Type
              </InputLabel>
              <Select
                labelId="auth-type-label"
                value={formData.authentication_type}
                onChange={(e) => setFormData({ ...formData, authentication_type: e.target.value })}
                label="Authentication Type"
                sx={{
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '12px',
                  },
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                    },
                  },
                }}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="bearer_token">Bearer Token</MenuItem>
                <MenuItem value="api_key">API Key</MenuItem>
                <MenuItem value="basic_auth">Basic Auth</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {formData.authentication_type === 'bearer_token' && (
            <Box sx={{ mb: 1.5 }}>
              <TextField
                label="Bearer Token"
                type="password"
                value={formData.authentication_config.token || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  authentication_config: { ...formData.authentication_config, token: e.target.value },
                })}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                        borderWidth: '2px',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                        borderWidth: '2px',
                        boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea',
                  },
                }}
              />
            </Box>
          )}

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  label="Retry Count"
                  type="number"
                  value={formData.retry_count}
                  onChange={(e) => setFormData({ ...formData, retry_count: parseInt(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0, max: 10 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea',
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  label="Timeout (seconds)"
                  type="number"
                  value={formData.timeout_seconds}
                  onChange={(e) => setFormData({ ...formData, timeout_seconds: parseInt(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 1, max: 300 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#667eea',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#667eea',
                    },
                  }}
                />
              }
              label="Active"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontWeight: 600,
                  color: '#667eea',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.4)',
              background: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name || !formData.url}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: 'rgba(102, 126, 234, 0.5)',
            },
          }}
        >
          {loading
            ? mode === 'create'
              ? 'Creating...'
              : 'Updating...'
            : mode === 'create'
            ? 'Create'
            : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
